import React, { useState } from 'react';
import { debugEmailJS, sendTestEmail, testEmailConfiguration } from '../utils/emailTest';
import { simpleEmailTest } from '../utils/simpleEmailTest';
import { testEmailTemplateParams } from '../utils/emailTemplateTest';
import { checkTemplateVariables } from '../utils/emailTemplateChecker';

export default function EmailDebugger() {
  const [testResult, setTestResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const handleConfigTest = async () => {
    setIsLoading(true);
    console.log('🧪 Testing EmailJS configuration...');
    
    try {
      const result = await debugEmailJS();
      
      setTestResult({
        type: 'config',
        success: result.success,
        message: result.success ? 'Configuration is valid' : 'Configuration has issues',
        details: result
      });
    } catch (error) {
      setTestResult({
        type: 'config',
        success: false,
        message: `Configuration test failed: ${error.message}`
      });
    }
    setIsLoading(false);
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }
    
    setIsLoading(true);
    console.log('📧 Sending test email to:', testEmail);
    
    // Try simple test first
    const result = await simpleEmailTest(testEmail);
    setTestResult({
      type: 'email',
      success: result.success,
      message: result.success ? 'Test email sent successfully!' : `Failed: ${result.error}`,
      details: result
    });
    setIsLoading(false);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>📧 Email Service Debugger</h2>
      
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Environment Variables Check</h3>
        <p><strong>Service ID:</strong> {process.env.REACT_APP_EMAILJS_SERVICE_ID || 'Not set'}</p>
        <p><strong>Template ID:</strong> {process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'Not set'}</p>
        <p><strong>Public Key:</strong> {process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? 'Set' : 'Not set'}</p>
        
        <button 
          onClick={handleConfigTest} 
          disabled={isLoading}
          style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
        >
          {isLoading ? 'Testing...' : 'Test Configuration'}
        </button>
      </div>

      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h3>Send Test Email</h3>
        <input
          type="email"
          placeholder="Enter test email address"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          style={{ width: '100%', padding: '10px', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '5px' }}
        />
        
        <button 
          onClick={handleSendTest} 
          disabled={isLoading || !testEmail}
          style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '10px' }}
        >
          {isLoading ? 'Sending...' : 'Send Test Email'}
        </button>
        
        <button 
          onClick={() => {
            console.log('📧 Manual console test - Check browser console for detailed logs');
            console.log('EmailJS config:', {
              serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
              templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
              publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? 'Set' : 'Missing'
            });
          }}
          style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '10px' }}
        >
          Console Debug
        </button>
        
        <button 
          onClick={async () => {
            if (!testEmail) {
              alert('Please enter a test email address');
              return;
            }
            setIsLoading(true);
            try {
              const result = await checkTemplateVariables(testEmail);
              setTestResult(result);
              console.log('🧪 Template variable test result:', result);
            } catch (error) {
              console.error('Template test error:', error);
              setTestResult({ success: false, error: error.message });
            } finally {
              setIsLoading(false);
            }
          }}
          style={{ padding: '10px 20px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
        >
          {isLoading ? 'Testing...' : 'Test Template Variables'}
        </button>
        
        <button 
          onClick={async () => {
            if (!testEmail) {
              alert('Please enter a test email address');
              return;
            }
            setIsLoading(true);
            const results = await testEmailTemplateParams(testEmail);
            setTestResult({
              type: 'template-test',
              success: results.some(r => r.success),
              message: `Template test completed. ${results.filter(r => r.success).length}/${results.length} formats succeeded.`,
              details: results
            });
            setIsLoading(false);
          }}
          disabled={isLoading || !testEmail}
          style={{ padding: '10px 20px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
        >
          {isLoading ? 'Testing...' : 'Test Template Formats'}
        </button>
      </div>

      {testResult && (
        <div style={{ 
          padding: '15px', 
          border: `1px solid ${testResult.success ? '#28a745' : '#dc3545'}`, 
          borderRadius: '5px',
          backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
          color: testResult.success ? '#155724' : '#721c24'
        }}>
          <h4>{testResult.success ? '✅ Success' : '❌ Error'}</h4>
          <p>{testResult.message}</p>
          {testResult.details && (
            <details>
              <summary>View Details</summary>
              <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                {JSON.stringify(testResult.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
        <h4>📋 Troubleshooting Steps:</h4>
        <ol>
          <li>Check if all EmailJS environment variables are set correctly</li>
          <li>Verify EmailJS service is active in your EmailJS dashboard</li>
          <li>Ensure the template ID matches your EmailJS template</li>
          <li>Check browser console for detailed error messages</li>
          <li>Try sending a test email to verify the service works</li>
        </ol>
      </div>
    </div>
  );
}
